import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowRightFromBracket, viPlus } from "@sippy-platform/valkyrie";

import { useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

function OutboundList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const outboundRuleStore = useSelector((state) => state.domain.outboundRules);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time outboundList or searchQuery changes
  const searchResults = useSearch(outboundRuleStore.list, searchValue, ["name", "prefix"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getOutbounds()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!id && matchesMdUp && outboundRuleStore.meta.lastFetched) {
      if (outboundRuleStore.list.length >= 1) {
        navigate(`${outboundRuleStore.list[0].id}`);
      } else {
        navigate("new");
      }
    }
  }, [id, matchesMdUp, navigate, outboundRuleStore.list, outboundRuleStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterListVirtualized
        fabBar
        title="Outbound rules"
        aria-label="Outbound rules list"
        id="outbound-list"
        list={searchResults}
        listItemProps={{
          primary: (x) => x.name,
          secondary: (x) => x.prefix,
          to: (x) => `../${x.id}`,
          selected: (x) => x.id === Number(id),
          icon: viArrowRightFromBracket,
        }}
        hideContentLoader={outboundRuleStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: outboundRuleStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any outbound rules yet",
          noResults: "No outbound rules in your current query",
          fetching: "Getting your outbound rules",
          icon: viArrowRightFromBracket,
        }}
      />
      <FabBar>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </>
  );
}

export default OutboundList;
